import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Landmark, ScrollText, Drama, Crown, School, Tv, Globe, Sparkles } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { fadeUp, viewportOnce } from "../../lib/motion";
import { useLang } from "../../i18n/LanguageContext";

const ICONS = [Landmark, ScrollText, Drama, Crown, School, Tv, Globe, Sparkles];

const Node = ({ item, index, highlight }) => {
  const Icon = ICONS[index] || Sparkles;
  const isEven = index % 2 === 0;
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      className={`relative flex items-center gap-6 md:gap-0 ${isEven ? "md:flex-row" : "md:flex-row-reverse"}`}
    >
      <div className={`md:w-1/2 ${isEven ? "md:pr-14 md:text-right" : "md:pl-14"}`}>
        <div className={`rounded-2xl p-6 ${highlight ? "glass border-gold-light/50 glow-gold" : "glass"}`} data-testid={`timeline-node-${index}`}>
          <div className={`mb-3 flex items-center gap-3 ${isEven ? "md:justify-end" : ""}`}>
            <span className={`flex h-10 w-10 items-center justify-center rounded-full ${highlight ? "bg-gradient-to-br from-gold-light to-gold-primary text-ink-900" : "bg-white/5 text-gold-light"}`}>
              <Icon className="h-5 w-5" />
            </span>
            <h3 className={`font-display text-2xl font-medium ${highlight ? "text-gold-gradient" : "text-white"}`}>{item.era}</h3>
          </div>
          <p className="text-sm leading-relaxed text-white/65">{item.note}</p>
        </div>
      </div>
      <div className="absolute left-0 flex h-full items-center md:left-1/2 md:-translate-x-1/2">
        <span className={`relative z-10 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-ink-800 ${highlight ? "bg-gold-light" : "bg-gold-primary"}`}>
          {highlight && <span className="absolute h-8 w-8 animate-ping rounded-full bg-gold-light/40" />}
        </span>
      </div>
      <div className="hidden md:block md:w-1/2" />
    </motion.div>
  );
};

export const EvolutionTimeline = () => {
  const { t } = useLang();
  const s = t.evolution;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.5", "end 0.6"] });
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="evolution" data-testid="evolution-section" className="relative overflow-hidden bg-ink-800 py-24 md:py-32 grain">
      <div className="relative z-10 mx-auto max-w-6xl px-6 md:px-8">
        <SectionHeading eyebrow={s.eyebrow} title={s.title} accent={s.accent} subtitle={s.subtitle} testid="evolution-heading" />
        <div ref={ref} className="relative mt-20 space-y-10 pl-8 md:space-y-16 md:pl-0">
          <div className="absolute left-[7px] top-0 h-full w-[2px] bg-white/10 md:left-1/2 md:-translate-x-1/2">
            <motion.div style={{ scaleY, transformOrigin: "top" }} className="h-full w-full bg-gradient-to-b from-gold-light via-gold-primary to-gold-deep" />
          </div>
          {s.items.map((item, i) => (
            <Node key={i} item={item} index={i} highlight={i === s.items.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
};
